import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Card,
  Statistic,
  Tag,
  Typography,
  Row,
  Col,
  Button,
  Input,
  Select,
  Space,
  Spin,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  message,
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  DollarOutlined,
  FundOutlined,
  RiseOutlined,
  CheckCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import moment from "moment";

const { Text, Title } = Typography;

const zambianColors = {
  green: "#198754",
  orange: "#de7921",
  red: "#da291c",
  black: "#000000",
};

const JutemInvestmentsPage = () => {
  const { company } = useContext(AppContext);
  const navigate = useNavigate();

  const [investments, setInvestments] = useState([]);
  const [interestExpenses, setInterestExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editForm] = Form.useForm();

  const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL || "https://zedlenders.pickmesms.com";

  useEffect(() => {
    if (company?.id !== 22) {
      message.warning("This page is only for Jutem Fund (Company ID 22)");
      navigate("/");
      return;
    }
    fetchData();
  }, [company]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch investments
      const investmentsRes = await axios.get(`${DIRECTUS_URL}/items/jutem_investments`, {
        headers,
        params: {
          filter: { company: { _eq: 22 } },
          fields: "id,reference_number,investor.id,investor.name,principal_amount,investment_date,year,status",
          limit: -1,
        },
      });

      // Fetch interest expenses (transactions)
      const expensesRes = await axios.get(`${DIRECTUS_URL}/items/transactions`, {
        headers,
        params: {
          filter: {
            company: { _eq: 22 },
            transaction_type: { _eq: "interest_expense" },
          },
          fields: "id,reference_number,notes,amount,transaction_date,investment_reference",
          limit: -1,
        },
      });

      setInvestments(investmentsRes.data.data || []);
      setInterestExpenses(expensesRes.data.data || []);
    } catch (error) {
      console.error("Error fetching investments:", error);
      message.error("Failed to load investments data");
    } finally {
      setLoading(false);
    }
  };

  const availableYears = useMemo(() => {
    const years = [...new Set(investments.map((inv) => inv.year).filter(Boolean))].sort().reverse();
    return years;
  }, [investments]);

  const filteredData = useMemo(() => {
    let filtered = [...investments];

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter((inv) =>
        (inv.investor?.name || "").toLowerCase().includes(search)
      );
    }

    if (selectedYear) {
      filtered = filtered.filter((inv) => inv.year === selectedYear);
    }

    if (selectedStatus) {
      filtered = filtered.filter((inv) =>
        (inv.status || "").toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    return filtered;
  }, [investments, searchText, selectedYear, selectedStatus]);

  const stats = useMemo(() => {
    const totalInvested = filteredData.reduce(
      (sum, inv) => sum + parseFloat(inv.principal_amount || 0),
      0
    );

    const activeInvestments = filteredData.filter(
      (inv) => (inv.status || "").toLowerCase() === "active"
    ).length;

    const maturedInvestments = filteredData.filter(
      (inv) => (inv.status || "").toLowerCase() === "matured"
    ).length;

    const filteredIds = new Set(filteredData.map((inv) => String(inv.id)));
    const totalInterestExpenses = interestExpenses
      .filter((exp) => filteredIds.has(String(exp.investment_reference)))
      .reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);

    return {
      totalInvested,
      activeInvestments,
      maturedInvestments,
      totalInterestExpenses,
    };
  }, [filteredData, interestExpenses]);

  const fmtK = (val) => {
    const num = parseFloat(val || 0);
    return `K ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      principal_amount: parseFloat(record.principal_amount || 0),
      investment_date: record.investment_date ? moment(record.investment_date) : null,
      year: record.year,
      status: record.status,
      reference_number: record.reference_number,
    });
    setEditModalVisible(true);
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      setSaving(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      await axios.patch(
        `${DIRECTUS_URL}/items/jutem_investments/${editingRecord.id}`,
        {
          principal_amount: values.principal_amount,
          investment_date: values.investment_date
            ? values.investment_date.format("YYYY-MM-DD")
            : null,
          year: values.year,
          status: values.status,
          reference_number: values.reference_number,
        },
        { headers }
      );

      message.success("Investment updated successfully");
      setEditModalVisible(false);
      setEditingRecord(null);
      editForm.resetFields();
      fetchData();
    } catch (error) {
      console.error("Error updating investment:", error);
      message.error("Failed to update investment");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    if (filteredData.length === 0) {
      message.warning("No data to export");
      return;
    }

    const exportData = filteredData.map((inv, i) => ({
      "#": i + 1,
      Investor: inv.investor?.name || "",
      "Reference Number": inv.reference_number || "",
      "Principal Amount (ZMW)": parseFloat(inv.principal_amount || 0).toFixed(2),
      "Investment Date": inv.investment_date || "",
      Year: inv.year || "",
      Status: inv.status || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Jutem Investments");
    XLSX.writeFile(wb, `Jutem_Investments_${moment().format("YYYY-MM-DD")}.xlsx`);
    message.success("Exported successfully");
  };

  const expandedRowRender = (record) => {
    // Get interest expenses for this investment
    const relatedExpenses = interestExpenses.filter(
      (exp) => String(exp.investment_reference) === String(record.id)
    );

    if (relatedExpenses.length === 0) {
      return (
        <div style={{ padding: 16, textAlign: "center" }}>
          <Text type="secondary">No interest expense transactions found</Text>
        </div>
      );
    }

    const columns = [
      {
        title: "Reference",
        dataIndex: "reference_number",
        key: "reference_number",
      },
      {
        title: "Date",
        dataIndex: "transaction_date",
        key: "transaction_date",
        render: (v) => (v ? moment(v).format("DD MMM YYYY") : "-"),
      },
      {
        title: "Notes",
        dataIndex: "notes",
        key: "notes",
        render: (v) => v || "-",
      },
      {
        title: "Interest Expense",
        dataIndex: "amount",
        key: "amount",
        render: (v) => (
          <Text style={{ color: zambianColors.red, fontWeight: "bold" }}>
            {fmtK(v)}
          </Text>
        ),
        align: "right",
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={relatedExpenses}
        rowKey="id"
        pagination={false}
        size="small"
        style={{ margin: "0 16px 16px 16px" }}
      />
    );
  };

  const columns = [
    {
      title: "Investor",
      key: "investor",
      render: (_, record) => (
        <Space>
          <Text strong>{record.investor?.name || "N/A"}</Text>
          {record.status?.toLowerCase() === "matured" && (
            <Tag color="success" icon={<CheckCircleOutlined />}>
              MATURED
            </Tag>
          )}
          {record.status?.toLowerCase() === "active" && (
            <Tag color="processing">ACTIVE</Tag>
          )}
        </Space>
      ),
      sorter: (a, b) =>
        (a.investor?.name || "").localeCompare(b.investor?.name || ""),
    },
    {
      title: "Reference",
      dataIndex: "reference_number",
      key: "reference_number",
      render: (v) => <Text code>{v}</Text>,
    },
    {
      title: "Principal Amount",
      dataIndex: "principal_amount",
      key: "principal_amount",
      render: (v) => fmtK(v),
      sorter: (a, b) => parseFloat(a.principal_amount || 0) - parseFloat(b.principal_amount || 0),
      align: "right",
    },
    {
      title: "Investment Date",
      dataIndex: "investment_date",
      key: "investment_date",
      render: (v) => (v ? moment(v).format("DD MMM YYYY") : "-"),
      sorter: (a, b) =>
        (a.investment_date || "").localeCompare(b.investment_date || ""),
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => {
        const statusLower = (s || "").toLowerCase();
        let color = "default";
        if (statusLower === "active") color = "blue";
        if (statusLower === "matured") color = "green";
        if (statusLower === "returned") color = "cyan";
        if (statusLower === "defaulted") color = "red";
        return <Tag color={color}>{s || "N/A"}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
          size="small"
        />
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 100 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Loading Jutem Investments...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>
        <FundOutlined style={{ marginRight: 12, color: zambianColors.green }} />
        Jutem Fund Investments
      </Title>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.green,
              boxShadow: "0 2px 8px rgba(25, 135, 84, 0.2)",
              minHeight: 130,
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Total Invested
                </span>
              }
              value={stats.totalInvested}
              precision={2}
              prefix={<DollarOutlined style={{ color: "#fff", fontSize: 24 }} />}
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: "#1890ff",
              boxShadow: "0 2px 8px rgba(24, 144, 255, 0.2)",
              minHeight: 130,
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Active Investments
                </span>
              }
              value={stats.activeInvestments}
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: "#52c41a",
              boxShadow: "0 2px 8px rgba(82, 196, 26, 0.2)",
              minHeight: 130,
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Matured Investments
                </span>
              }
              value={stats.maturedInvestments}
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.red,
              boxShadow: "0 2px 8px rgba(218, 41, 28, 0.2)",
              minHeight: 130,
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Total Interest Expenses
                </span>
              }
              value={stats.totalInterestExpenses}
              precision={2}
              prefix={<RiseOutlined style={{ color: "#fff", fontSize: 24 }} />}
              valueStyle={{ color: "#fff", fontSize: 22, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Search by investor name..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Year"
              value={selectedYear}
              onChange={setSelectedYear}
              allowClear
              style={{ width: "100%" }}
            >
              {availableYears.map((y) => (
                <Select.Option key={y} value={y}>
                  {y}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Status"
              value={selectedStatus}
              onChange={setSelectedStatus}
              allowClear
              style={{ width: "100%" }}
            >
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="matured">Matured</Select.Option>
              <Select.Option value="returned">Returned</Select.Option>
              <Select.Option value="settled">Settled</Select.Option>
              <Select.Option value="defaulted">Defaulted</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={4} md={4}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              block
              style={{ background: zambianColors.green, borderColor: zambianColors.green }}
            >
              Export
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          expandable={{
            expandedRowRender,
            rowExpandable: (record) => true,
          }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `${total} investments`,
          }}
          scroll={{ x: true }}
          summary={() => {
            const totalPrincipal = filteredData.reduce(
              (s, inv) => s + parseFloat(inv.principal_amount || 0),
              0
            );

            return (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ fontWeight: "bold" }}>
                  <Table.Summary.Cell index={0}>TOTAL</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    {filteredData.length} investments
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    {fmtK(totalPrincipal)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} colSpan={4} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      {/* Edit Investment Modal */}
      <Modal
        title="Edit Investment"
        open={editModalVisible}
        onOk={handleEditSave}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingRecord(null);
          editForm.resetFields();
        }}
        confirmLoading={saving}
        okText="Save"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="reference_number"
            label="Reference Number"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="principal_amount"
            label="Principal Amount (ZMW)"
            rules={[{ required: true, message: "Please enter principal amount" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              precision={2}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(v) => v.replace(/,/g, "")}
            />
          </Form.Item>
          <Form.Item
            name="investment_date"
            label="Investment Date"
            rules={[{ required: true, message: "Please select a date" }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item
            name="year"
            label="Year"
          >
            <InputNumber style={{ width: "100%" }} min={2000} max={2100} />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select a status" }]}
          >
            <Select>
              <Select.Option value="Active">Active</Select.Option>
              <Select.Option value="Matured">Matured</Select.Option>
              <Select.Option value="Returned">Returned</Select.Option>
              <Select.Option value="Settled">Settled</Select.Option>
              <Select.Option value="Defaulted">Defaulted</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default JutemInvestmentsPage;
